
import { useForm } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { Button } from '~frontend/components/ui/button'
import { Checkbox } from "~frontend/components/ui/checkbox"
import { Input } from "~frontend/components/ui/input"
import { Label } from "~frontend/components/ui/label"
import { ScrollArea } from '~frontend/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~frontend/components/ui/select"
import { trpc } from '~frontend/router'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2, X } from 'lucide-react'
import { Textarea } from '~frontend/components/ui/textarea'

const domicileOptions = [
    "ANDAMAN & NICOBAR ISLANDS",
    "ANDHRA PRADESH",
    "ARUNACHAL PRADESH",
    "ASSAM",
    "BIHAR",
    "CHANDIGARH",
    "CHHATTISGARH",
    "DELHI",
    "DOMICILE",
    "Dadra and Nagar Haveli",
    "Daman and Diu",
    "GOA",
    "GUJARAT",
    "HARYANA",
    "HIMACHAL PRADESH",
    "JAMMU AND KASHMIR",
    "JHARKHAND",
    "KARNATAKA",
    "KERALA",
    "LADAKH",
    "LAKSHADWEEP",
    "MADHYA PRADESH",
    "MAHARASHTRA",
    "MANIPUR",
    "MEGHALAYA",
    "MIZORAM",
    "NAGALAND",
    "ODISHA",
    "PUDUCHERRY",
    "PUNJAB",
    "RAJASTHAN",
    "SIKKIM",
    "TAMIL NADU",
    "TELANGANA",
    "TRIPURA",
    "UTTAR PRADESH",
    "UTTARAKHAND",
    "WEST BENGAL"
]

const casteOptions = [
    "OC",
    "SC",
    "ST",
    "OBC",
    "OTHERS",
]
const categoryOptions = [
    "GENERAL",
    "DEFENCE",
    "OBC",
    "SC",
    "ST",
]

const parentRelationOptions = [
    "Father",
    "Mother",
    "Guardian",
    "Uncle",
    "Aunt",
    "Grandfather",
    "Grandmother"
]

const statusOptions = [
    "Admitted",
    "Joined",
    "Left"
]

const genderOptions = [
    "Male",
    "Female"
]

// trigger build 

interface ParentData {
    id: string;
    parentName: string;
    phoneNumbers: string[];
    occupation: string;
    monthlyIncome: number | null;
    relation: string;
    priority: number;
}
export const Route = createFileRoute('/_LoggedIn/cadet/application')({
    component: RouteComponent,
})

const studentApplicationSchema = z.object({
    name: z.string().nonempty({ message: 'Name is required' }),
    gender: z.enum(['Male', 'Female']),
    dateOfBirthDay: z.number().min(1).max(31),
    dateOfBirthMonth: z.number().min(1).max(12, { message: 'Month must be between 1 and 12' }),
    dateOfBirthYear: z.number().min(2014).max(2016, { message: 'Year must be between 2014 and 2016' }),
    aadhaarCardNumber: z.string().length(12).transform(val => val === "" ? undefined : val),
    placeOfBirth: z.string(),
    fatherName: z.string().nonempty({ message: 'Father\'s name is required' }),
    motherName: z.string().nonempty({ message: 'Mother\'s name is required' }),
    guardianName: z.string(),
    guardianRelation: z.string(),
    occupationOfWhichParent: z.enum(['mother', 'father', 'guardian']),
    occupation: z.string(),
    previousSchoolNameAndAddress: z.string(),
    isPreviousSchoolRecognized: z.boolean(),
    previousSchoolClass: z.number(),
    motherTongue: z.string(),
    nationality: z.string(),
    religion: z.string(),
    caste: z.enum(['GEN', 'OBC', 'SC', 'ST', 'DEF']).nullable(),
    category: z.enum(['GENERAL', 'OBC', 'SC', 'ST', 'DEFENCE']).nullable(),
    addressline: z.string(),
    city: z.string(),
    state: z.string(),
    pinCode: z.string(),
    domicile: z.string(),
    phoneNumber: z.array(z.string()),
    parents: z.array(z.object({
        id: z.string(),
        parentName: z.string().min(1, 'Parent name is required'),
        phoneNumbers: z.array(z.string()),
        occupation: z.string(),
        monthlyIncome: z.number().nullable(),
        relation: z.string().min(1, 'Relation is required'),
        priority: z.number(),
    })),
});

// Helper component for displaying form field errors
function FieldInfo({ field }: { field: any }) {
    return (
        <>
            {field.state.meta.isTouched && field.state.meta.errors.length > 0 ? (
                <em className="text-red-500 text-xs">{field.state.meta.errors.join(', ')}</em>
            ) : null}
            {field.state.meta.isValidating ? <em className="text-xs">Validating...</em> : null}
        </>
    )
}

// Sortable Phone Number Component
interface SortablePhoneItemProps {
    id: string;
    value: string;
    index: number;
    onRemove: (index: number) => void;
    onChange: (index: number, value: string) => void;
}

function SortablePhoneItem({ id, value, index, onRemove, onChange }: SortablePhoneItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className="flex items-center gap-2 mb-2">
            <div
                {...attributes}
                {...listeners}
                className="flex items-center justify-center w-6 h-6 cursor-grab active:cursor-grabbing"
            >
                <GripVertical className="w-4 h-4 text-gray-400" />
            </div>
            <Input
                type="tel"
                value={value}
                onChange={(e) => onChange(index, e.target.value)}
                placeholder="Enter phone number"
                className="flex-1"
            />
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onRemove(index)}
                className="text-red-500 hover:text-red-700"
            >
                <X className="w-4 h-4" />
            </Button>
        </div>
    );
}

// Sortable Parent Component
interface SortableParentItemProps {
    parent: ParentData;
    index: number;
    onRemove: (index: number) => void;
    onUpdate: (index: number, parent: ParentData) => void;
}

function SortableParentItem({ parent, index, onRemove, onUpdate }: SortableParentItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: parent.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handlePhoneReorder = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const activeIndex = parent.phoneNumbers.findIndex((_, i) => `phone-${i}` === active.id);
            const overIndex = parent.phoneNumbers.findIndex((_, i) => `phone-${i}` === over.id);

            const newPhoneNumbers = arrayMove(parent.phoneNumbers, activeIndex, overIndex);
            onUpdate(index, { ...parent, phoneNumbers: newPhoneNumbers });
        }
    };

    const addPhoneNumber = () => {
        const newPhoneNumbers = [...parent.phoneNumbers, ''];
        onUpdate(index, { ...parent, phoneNumbers: newPhoneNumbers });
    };

    const removePhoneNumber = (phoneIndex: number) => {
        const newPhoneNumbers = parent.phoneNumbers.filter((_, i) => i !== phoneIndex);
        onUpdate(index, { ...parent, phoneNumbers: newPhoneNumbers });
    };

    const updatePhoneNumber = (phoneIndex: number, value: string) => {
        const newPhoneNumbers = [...parent.phoneNumbers];
        newPhoneNumbers[phoneIndex] = value;
        onUpdate(index, { ...parent, phoneNumbers: newPhoneNumbers });
    };

    return (
        <div ref={setNodeRef} style={style} className="border rounded-lg p-4 mb-4 bg-white">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div
                        {...attributes}
                        {...listeners}
                        className="flex items-center justify-center w-6 h-6 cursor-grab active:cursor-grabbing"
                    >
                        <GripVertical className="w-4 h-4 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium">Parent {index + 1}</h3>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onRemove(index)}
                    className="text-red-500 hover:text-red-700"
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <Label htmlFor={`parent-name-${index}`}>Parent Name</Label>
                    <Input
                        id={`parent-name-${index}`}
                        type="text"
                        value={parent.parentName}
                        onChange={(e) => onUpdate(index, { ...parent, parentName: e.target.value })}
                        placeholder="Enter parent name"
                    />
                </div>
                <div>
                    <Label htmlFor={`parent-relation-${index}`}>Relation</Label>
                    <Select
                        value={parent.relation}
                        onValueChange={(value) => onUpdate(index, { ...parent, relation: value })}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select relation" />
                        </SelectTrigger>
                        <SelectContent>
                            {parentRelationOptions.map(option => (
                                <SelectItem key={option} value={option}>
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <Label htmlFor={`parent-occupation-${index}`}>Occupation</Label>
                    <Input
                        id={`parent-occupation-${index}`}
                        type="text"
                        value={parent.occupation}
                        onChange={(e) => onUpdate(index, { ...parent, occupation: e.target.value })}
                        placeholder="Enter occupation"
                    />
                </div>
                <div>
                    <Label htmlFor={`parent-income-${index}`}>Monthly Income</Label>
                    <Input
                        id={`parent-income-${index}`}
                        type="number"
                        value={parent.monthlyIncome || ''}
                        onChange={(e) => onUpdate(index, { ...parent, monthlyIncome: e.target.value ? Number(e.target.value) : null })}
                        placeholder="Enter monthly income"
                    />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <Label>Phone Numbers</Label>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addPhoneNumber}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Phone
                    </Button>
                </div>

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handlePhoneReorder}
                >
                    <SortableContext
                        items={parent.phoneNumbers.map((_, i) => `phone-${i}`)}
                        strategy={verticalListSortingStrategy}
                    >
                        {parent.phoneNumbers.map((phone, phoneIndex) => (
                            <SortablePhoneItem
                                key={`phone-${phoneIndex}`}
                                id={`phone-${phoneIndex}`}
                                value={phone}
                                index={phoneIndex}
                                onRemove={removePhoneNumber}
                                onChange={updatePhoneNumber}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
        </div>
    );
}

const PdfViewer = ({ pdfUrl }: { pdfUrl: string }) => {
    return (
        <div style={{ height: '100%' }}>
            <iframe
                src={pdfUrl}
                width="100%"
                height="600px"
                style={{ border: 'none' }}
                title="PDF Viewer"
            />
            <div className="mt-2 text-center">
                <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                >
                    Open PDF in new tab
                </a>
            </div>
        </div>
    );
};

export default PdfViewer;

// Parents Management Component
interface ParentsManagementProps {
    parents: ParentData[];
    onChange: (parents: ParentData[]) => void;
}

function ParentsManagement({ parents, onChange }: ParentsManagementProps) {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const activeIndex = parents.findIndex(parent => parent.id === active.id);
            const overIndex = parents.findIndex(parent => parent.id === over.id);

            const newParents = arrayMove(parents, activeIndex, overIndex);
            // Update priorities based on new order
            const updatedParents = newParents.map((parent, index) => ({
                ...parent,
                priority: index + 1,
            }));
            onChange(updatedParents);
        }
    };

    const addParent = () => {
        const newParent: ParentData = {
            id: Date.now().toString(),
            parentName: '',
            phoneNumbers: [''],
            occupation: '',
            monthlyIncome: null,
            relation: '',
            priority: parents.length + 1,
        };
        onChange([...parents, newParent]);
    };

    const removeParent = (index: number) => {
        const newParents = parents.filter((_, i) => i !== index);
        // Update priorities after removal
        const updatedParents = newParents.map((parent, i) => ({
            ...parent,
            priority: i + 1,
        }));
        onChange(updatedParents);
    };

    const updateParent = (index: number, updatedParent: ParentData) => {
        const newParents = [...parents];
        newParents[index] = updatedParent;
        onChange(newParents);
    };

    return (
        <div className="px-2 w-full">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Parents/Guardians Information</h2>
                <Button
                    type="button"
                    variant="outline"
                    onClick={addParent}
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Parent
                </Button>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={parents.map(parent => parent.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {parents.map((parent, index) => (
                        <SortableParentItem
                            key={parent.id}
                            parent={parent}
                            index={index}
                            onRemove={removeParent}
                            onUpdate={updateParent}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            {parents.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    No parents added yet. Click "Add Parent" to get started.
                </div>
            )}
        </div>
    );
}

function mapStudentDetailsToFormValues(studentDetails: any) {
    // Unpack date of birth
    let day = '', month = '', year = '';
    if (studentDetails.dateOfBirth) {
        const dateStr = studentDetails.dateOfBirth.toString();
        const [y, m, d] = dateStr.split('-');
        year = y;
        month = m;
        day = d;
    }

    // Map parents from backend data or provide defaults
    const mappedParents = studentDetails.parents && studentDetails.parents.length > 0
        ? studentDetails.parents.map((parent: any) => ({
            id: parent.id || Date.now().toString(),
            parentName: parent.parentName || '',
            phoneNumbers: parent.phoneNumbers && parent.phoneNumbers.length > 0 ? parent.phoneNumbers : [''],
            occupation: parent.occupation || '',
            monthlyIncome: parent.monthlyIncome,
            relation: parent.relation || '',
            priority: parent.priority || 1,
        }))
        : [
            {
                id: '1',
                parentName: '',
                phoneNumbers: [''],
                occupation: '',
                monthlyIncome: null,
                relation: '',
                priority: 1,
            },
            {
                id: '2',
                parentName: '',
                phoneNumbers: [''],
                occupation: '',
                monthlyIncome: null,
                relation: '',
                priority: 2,
            },
            {
                id: '3',
                parentName: '',
                phoneNumbers: [''],
                occupation: '',
                monthlyIncome: null,
                relation: '',
                priority: 3,
            },
        ];

    return {
        cadetName: studentDetails.cadetName || '',
        status: studentDetails.status || 'Admitted',
        gender: studentDetails.gender || 'Male',
        dateOfBirthDay: day,
        dateOfBirthMonth: month,
        dateOfBirthYear: year,
        aadhaarCardNumber: studentDetails.aadhaarCardNumber || '',
        placeOfBirth: studentDetails.placeOfBirth || '',
        guardianName: '',
        guardianRelation: '',
        occupationOfWhichParent: 'father' as const,
        occupation: '',
        previousSchoolNameAndAddress: studentDetails.previousSchoolNameAndAddress || '',
        isPreviousSchoolRecognized: studentDetails.isPreviousSchoolRecognized || false,
        previousSchoolClass: studentDetails.previousSchoolClass || 1,
        motherTongue: studentDetails.motherTongue || '',
        nationality: studentDetails.nationality || '',
        religion: studentDetails.religion || '',
        caste: studentDetails.caste || 'OC' as const,
        category: studentDetails.category || 'GENERAL' as const,
        addressline: studentDetails.addressLine || '',
        city: studentDetails.city || '',
        state: studentDetails.state || '',
        pinCode: studentDetails.pinCode || '',
        domicile: studentDetails.domicile || '',
        phoneNumber: [''],
        parents: mappedParents as ParentData[],
    };
}

function RouteComponent() {
    // State to track current student index
    const [currentStudentIndex, setCurrentStudentIndex] = React.useState(0);

    // Get query client for invalidating queries
    const queryClient = useQueryClient();

    // Create mutation options using tRPC
    const updateCadetMutationOptions = trpc.cadet.updateCadetData.mutationOptions({
        onSuccess: () => {
            toast.success('Cadet data updated successfully!', {
                description: 'All changes have been saved to the database.',
            });

            // Immediately update the query cache to reflect the change
            queryClient.setQueryData(
                trpc.cadet.getUnreviewedAdmissionNumbers.queryOptions().queryKey,
                (oldData: any) => {
                    if (!oldData) return oldData;

                    // Remove the current student from pending list
                    const newPendingCadetData = oldData.pendingCadetData.filter(
                        (_: any, index: number) => index !== currentStudentIndex
                    );

                    return {
                        ...oldData,
                        pendingCadetData: newPendingCadetData
                    };
                }
            );

            // Handle navigation after removing the current student
            if (pendingStudents) {
                const remainingStudents = pendingStudents.pendingCadetData.length - 1; // -1 because we just removed one

                if (remainingStudents === 0) {
                    toast.success('All applications reviewed!', {
                        description: 'You have completed reviewing all pending applications.',
                    });
                } else if (currentStudentIndex >= remainingStudents) {
                    // If we were at the last student, go to the new last student
                    setCurrentStudentIndex(remainingStudents - 1);
                }
                // Otherwise, stay at current index since the array shifted and the next student is now at this position
            }

            // Invalidate to sync with server (this will happen in background)
            queryClient.invalidateQueries({
                queryKey: trpc.cadet.getUnreviewedAdmissionNumbers.queryOptions().queryKey
            });
        },
        onError: (error: any) => {
            console.error('Error updating cadet data:', error);
            toast.error('Failed to update cadet data', {
                description: error.message || 'Please try again later.',
            });
        }
    });

    // Use the mutation options with useMutation
    const updateCadetMutation = useMutation(updateCadetMutationOptions);

    const form = useForm({
        defaultValues: {
            cadetName: '',
            status: 'Admitted',
            gender: 'Male',
            dateOfBirthDay: '',
            dateOfBirthMonth: '',
            dateOfBirthYear: '',
            aadhaarCardNumber: '',
            placeOfBirth: '',
            guardianName: '',
            guardianRelation: '',
            occupationOfWhichParent: 'father',
            occupation: '',
            previousSchoolNameAndAddress: '',
            isPreviousSchoolRecognized: false,
            previousSchoolClass: 1,
            motherTongue: '',
            nationality: '',
            religion: '',
            caste: 'OC',
            category: 'GENERAL',
            addressline: '',
            city: '',
            state: '',
            pinCode: '',
            domicile: '',
            phoneNumber: [''],
            parents: [
                {
                    id: '1',
                    parentName: '',
                    phoneNumbers: [''],
                    occupation: '',
                    monthlyIncome: null,
                    relation: '',
                    priority: 1,
                },
                {
                    id: '2',
                    parentName: '',
                    phoneNumbers: [''],
                    occupation: '',
                    monthlyIncome: null,
                    relation: '',
                    priority: 2,
                },
                {
                    id: '3',
                    parentName: '',
                    phoneNumbers: [''],
                    occupation: '',
                    monthlyIncome: null,
                    relation: '',
                    priority: 3,
                },
            ] as ParentData[],
        },
        onSubmit: async ({ value }) => {
            const currentStudent = pendingStudents?.pendingCadetData[currentStudentIndex];
            if (!currentStudent) {
                toast.error('No student selected for review');
                return;
            }

            updateCadetMutation.mutate({
                uniqueId: currentStudent.uniqueId,
                ...value,
                // Ensure all fields are properly typed
                gender: value.gender as "Male" | "Female",
                caste: value.caste as "OBC" | "SC" | "ST" | "OC" | "Others" | null,
                category: value.category as "GENERAL" | "DEFENCE" | "OBC" | "SC" | "ST" | null
            });
        },
    });
    const FormField = form.Field;
    const { data: pendingStudents, isLoading: isPendingStudentsLoading } = useQuery(trpc.cadet.getUnreviewedAdmissionNumbers.queryOptions());

    // Get current student uniqueId
    const currentStudentUniqueId = pendingStudents?.pendingCadetData[currentStudentIndex]?.uniqueId;

    const { data: studentDetails, isLoading: isStudentDetailsLoading } = useQuery({
        ...trpc.cadet.getCadetData.queryOptions({
            uniqueId: currentStudentUniqueId || undefined,
            admissionNumber: undefined,
        }),
        enabled: !!currentStudentUniqueId
    });

    // Set form values from studentDetails when loaded
    React.useEffect(() => {
        if (studentDetails) {
            const mappedValues = mapStudentDetailsToFormValues(studentDetails);
            form.reset(mappedValues);
        }
    }, [studentDetails, form]);

    // Reset form when student index changes
    React.useEffect(() => {
        if (currentStudentUniqueId && !studentDetails) {
            // Reset to default values when switching students
            form.reset();
        }
    }, [currentStudentIndex, currentStudentUniqueId, studentDetails, form]);

    if (isStudentDetailsLoading && isPendingStudentsLoading) {
        return (
            <div className="flex items-center justify-center min-h-full">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
                <span className="ml-4 text-xl">Loading...</span>
            </div>
        );
    } else {
        console.log(studentDetails)
        console.log(pendingStudents)
    }

    // Navigation functions
    const goToPrevious = () => {
        if (currentStudentIndex > 0) {
            setCurrentStudentIndex(prev => prev - 1);
            form.reset(); // Reset form when navigating
        }
    };

    const goToNext = () => {
        if (pendingStudents && currentStudentIndex < pendingStudents.pendingCadetData.length - 1) {
            setCurrentStudentIndex(prev => prev + 1);
            form.reset(); // Reset form when navigating
        }
    };

    const currentStudent = pendingStudents?.pendingCadetData[currentStudentIndex];
    const totalStudents = pendingStudents?.totalCadetCount || 0;
    const reviewedCount = totalStudents - (pendingStudents?.pendingCadetData.length || 0);
    const progressPercentage = totalStudents > 0 ? (reviewedCount / totalStudents) * 100 : 0;

    if (isPendingStudentsLoading) {
        return null;
    }

    if (!pendingStudents || pendingStudents?.pendingCadetData?.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-full">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">All Applications Reviewed!</h2>
                    <p className="text-gray-600">There are no pending applications to review.</p>
                </div>
            </div>
        );
    }

    return <div className="px-2 overflow-hidden flex flex-col">
        {/* Header Section - Fixed Height */}
        <div className='p-4 border-b bg-gray-50 flex-shrink-0'>
            {/* Progress Section */}
            <div>
                <div className='flex items-center justify-between mb-2'>
                    <p className='text-xl font-semibold'>
                        Admission Number: {currentStudent?.admissionNumber || studentDetails?.admissionNumber}
                    </p>
                    <div className='text-sm text-gray-600'>
                        Reviewing {currentStudentIndex + 1} of {pendingStudents.pendingCadetData.length} remaining
                    </div>
                </div>

                {/* Progress Bar */}
                <div className='w-full bg-gray-200 rounded-full h-2 mb-2'>
                    <div
                        className='bg-blue-600 h-2 rounded-full transition-all duration-300'
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>

                <div className='flex items-center justify-between text-sm text-gray-600'>
                    <span>Total Progress: {reviewedCount} / {totalStudents} applications reviewed</span>
                    <span>{progressPercentage.toFixed(2)}% complete</span>
                </div>
            </div>

            {/* Navigation Controls */}
            <div className='flex gap-2 mt-3'>
                <Button
                    variant="outline"
                    onClick={goToPrevious}
                    disabled={currentStudentIndex === 0}
                    size="sm"
                >
                    ← Previous
                </Button>
                <Button
                    variant="outline"
                    onClick={goToNext}
                    disabled={currentStudentIndex >= pendingStudents.pendingCadetData.length - 1}
                    size="sm"
                >
                    Next →
                </Button>
            </div>
        </div>

        {/* Main Content - Takes Remaining Height */}
        <div className='grid grid-cols-2 gap-2 flex-1 min-h-0'>
            <ScrollArea className='h-full mt-2'>
                <form
                    onSubmit={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.handleSubmit();
                    }}
                    className="space-y-4 px-16 flex gap-3 flex-col"
                >
                    <FormField name="cadetName">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="cadetName">Full Name</Label>
                                <Input type="text" id="cadetName" value={field.state.value} onChange={e => field.handleChange(e.target.value)} onBlur={e => field.handleChange(e.target.value.toUpperCase())} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="status">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="status">Status</Label>
                                <Select value={field.state.value} onValueChange={value => field.handleChange(value as "Admitted" | "Joined" | "Left")}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {statusOptions.map(option => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="gender">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="gender">Gender</Label>
                                <Select value={field.state.value} onValueChange={value => field.handleChange(value as "Male" | "Female")}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select gender" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {genderOptions.map(option => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <div>
                        <Label className='mb-1'>Date of Birth</Label>
                        <div className='flex gap-3 w-full'>
                            <FormField name="dateOfBirthDay">
                                {(field: any) => (
                                    <Input
                                        type="text"
                                        placeholder="DD"
                                        maxLength={2}
                                        value={field.state.value}
                                        onChange={e => field.handleChange(e.target.value)}
                                        className="w-16"
                                    />
                                )}
                            </FormField>
                            <FormField name="dateOfBirthMonth">
                                {(field: any) => (
                                    <Input
                                        type="text"
                                        placeholder="MM"
                                        pattern="^(0[1-9]|1[0-2])$"
                                        maxLength={2}
                                        value={field.state.value}
                                        onChange={e => field.handleChange(e.target.value)}
                                        className="w-16"
                                    />
                                )}
                            </FormField>
                            <FormField name="dateOfBirthYear">
                                {(field: any) => (
                                    <Input
                                        type="text"
                                        pattern="^(19|20)\d{2}$"
                                        maxLength={4}
                                        placeholder="YYYY"
                                        value={field.state.value}
                                        onChange={e => field.handleChange(e.target.value)}
                                        className="w-24"
                                    />
                                )}
                            </FormField>
                        </div>
                    </div>
                    <FormField name="aadhaarCardNumber">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="aadhaarCardNumber">Aadhaar Number</Label>
                                <Input type="text" id="aadhaarCardNumber" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="placeOfBirth">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="placeOfBirth">Place of Birth</Label>
                                <Input type="text" id="placeOfBirth" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>

                    {/* Parents Management Section */}
                    <FormField name="parents">
                        {(field: any) => (
                            <ParentsManagement
                                parents={field.state.value}
                                onChange={field.handleChange}
                            />
                        )}
                    </FormField>

                    <FormField name="previousSchoolNameAndAddress">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="previousSchoolNameAndAddress">Previous School Name & AddressLine</Label>
                                <Input type="text" id="previousSchoolNameAndAddress" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="isPreviousSchoolRecognized">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="isPreviousSchoolRecognized"
                                        checked={field.state.value}
                                        onCheckedChange={checked => field.handleChange(checked)}
                                    />
                                    <Label htmlFor="isPreviousSchoolRecognized">Is Previous School Recognized?</Label>
                                </div>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="previousSchoolClass">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="previousSchoolClass">Previous School Class</Label>
                                <Input type="text" id="previousSchoolClass" value={field.state.value} onChange={e => field.handleChange(Number(e.target.value))} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="motherTongue">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="motherTongue">Mother Tongue</Label>
                                <Input type="text" id="motherTongue" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="nationality">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="nationality">Nationality</Label>
                                <Input type="text" id="nationality" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="religion">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="religion">Religion</Label>
                                <Input type="text" id="religion" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="caste">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="caste">Caste</Label>
                                <Select value={field.state.value || ''} onValueChange={value => field.handleChange(value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select caste" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {casteOptions.map(option => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="category">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="category">Category</Label>
                                <Select value={field.state.value || ''} onValueChange={value => field.handleChange(value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categoryOptions.map(option => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="addressline">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="addressline">Address Line</Label>
                                <Textarea id="addressline" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="city">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="city">City</Label>
                                <Input type="text" id="city" value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="state">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="state">State</Label>
                                <Select name="state" value={field.state.value} onValueChange={value => field.handleChange(value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select State" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {domicileOptions.map(option => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="pinCode">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="pinCode">Pin Code</Label>
                                <Input type="text" id="pinCode" minLength={6} maxLength={6} value={field.state.value} onChange={e => field.handleChange(e.target.value)} />
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>
                    <FormField name="domicile">
                        {(field: any) => (
                            <div className="grid w-full max-w-sm items-center gap-1">
                                <Label htmlFor="domicile">Domicile</Label>
                                <Select name="domicile" value={field.state.value} onValueChange={value => field.handleChange(value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select domicile" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {domicileOptions.map(option => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                            </div>
                        )}
                    </FormField>

                    <Button
                        type="submit"
                        className="btn btn-primary mt-4 mb-8"
                        disabled={updateCadetMutation.isPending}
                    >
                        {updateCadetMutation.isPending ? 'Updating...' : 'Submit'}
                    </Button>
                </form>
            </ScrollArea>

            <ScrollArea className='h-full'>
                {studentDetails?.applicationPDF ? (
                    <PdfViewer pdfUrl={studentDetails.applicationPDF} />
                ) : (
                    <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center">
                            {isStudentDetailsLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading PDF...</p>
                                </>
                            ) : (
                                <p className="text-gray-600">No PDF available for this application</p>
                            )}
                        </div>
                    </div>
                )}
            </ScrollArea>
        </div>
    </div>
}
